import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import QRCode from 'qrcode';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import QRScanner from '../../../components/QRScanner';
import { mfetch, MemberApiError } from '../api';
import Icon from '../design/Icon';
import { GymHeader, Sheet, fmtDateShort } from '../design/atoms';
import type { MemberGym } from '../hooks/useMemberGyms';

/* SPEC 02 Check-in QR, adapted to the real mechanism: the member shows a
   static QR (their phone number) that the front desk scans, or scans the
   gym's branch QR themselves. */

interface OutletCtx {
  gyms: MemberGym[];
  activeGym: MemberGym | null;
  activeGymId: string | null;
  setActiveGymId: (id: string | null) => void;
  hasClassScheduling: boolean;
  loading: boolean;
}

interface CheckinLog {
  id: string;
  checkInTime: string;
  branch: { displayName: string } | null;
}

interface BillingMembership {
  status: string | null;
  endDate: string | null;
  plan: { name: string } | null;
}

function isoToYmd(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function MemberCheckInPage() {
  const { token, member } = useMemberAuth();
  const { activeGym, activeGymId, loading: gymsLoading } = useOutletContext<OutletCtx>();
  const navigate = useNavigate();

  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(true);
  const [qrError, setQrError] = useState<string | null>(null);
  const [recent, setRecent] = useState<CheckinLog[]>([]);
  const [tier, setTier] = useState<string | null>(null);
  const [scanOpen, setScanOpen] = useState(false);

  const loadQr = useCallback(() => {
    if (!token) return;
    setQrLoading(true);
    setQrError(null);
    mfetch(token, '/member/qr')
      .then(r => QRCode.toDataURL(r.data.qrValue, { width: 372, margin: 1 }))
      .then(url => setQrDataUrl(url))
      .catch(e => setQrError(e instanceof MemberApiError ? e.message : 'Could not load your code'))
      .finally(() => setQrLoading(false));
  }, [token]);

  const loadRecent = useCallback(() => {
    if (!token || !activeGymId) return;
    mfetch(token, '/member/checkins', { params: { gymId: activeGymId, limit: '3' } })
      .then(r => setRecent(r.data ?? []))
      .catch(() => setRecent([]));
  }, [token, activeGymId]);

  useEffect(loadQr, [loadQr]);
  useEffect(loadRecent, [loadRecent]);

  // Membership tier for the header tag — best effort, skipped if unknown.
  useEffect(() => {
    if (!token || !activeGymId) return;
    mfetch(token, '/member/billing', { params: { gymId: activeGymId } })
      .then(r => {
        const items: BillingMembership[] = r.data ?? [];
        const now = new Date();
        const active = items.find(
          m => (m.status ?? '').toLowerCase() === 'active' && m.endDate && new Date(m.endDate) >= now
        );
        setTier(active?.plan?.name ?? null);
      })
      .catch(() => setTier(null));
  }, [token, activeGymId]);

  if (!gymsLoading && !activeGymId) {
    return (
      <div className="mpage">
        <div className="dp dp-l" style={{ paddingTop: 8 }}>Check in</div>
        <div className="card flat"><div className="sub wrap">Join a gym to check in.</div></div>
      </div>
    );
  }

  const gymName = activeGym?.displayName || activeGym?.name || 'Your gym';
  const memberName = member?.displayName || member?.fullName || member?.phoneNumber || 'Member';

  return (
    <div className="mpage">
      {/* Reached from the Gym header's QR button, not the tab bar — needs a way back. */}
      <div className="row" style={{ gap: 10 }}>
        <button className="icon-btn" aria-label="Back to gym" onClick={() => navigate('/app')}>
          <Icon n="left" s={18} c="var(--ink-2)" />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <GymHeader
            name={gymName}
            logoUrl={activeGym?.logoUrl}
            right={tier ? <span className="tag gold trunc" style={{ maxWidth: 140 }}>{tier}</span> : undefined}
          />
        </div>
      </div>

      <div className="col" style={{ alignItems: 'center', gap: 2, paddingTop: 6 }}>
        <div className="dp dp-l">Check in</div>
        <div className="sub">Show this at the front desk</div>
      </div>

      {/* QR card */}
      <div
        className="card"
        style={{
          borderColor: 'var(--gold-line)',
          borderRadius: 'var(--r-l)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
          padding: '22px 20px 18px',
        }}
      >
        {qrLoading ? (
          <div className="skel" style={{ width: 186, height: 186 }} />
        ) : qrError ? (
          <div className="col" style={{ alignItems: 'center', gap: 10, height: 186, justifyContent: 'center' }}>
            <div className="sub wrap" style={{ textAlign: 'center' }}>{qrError}</div>
            <button className="btn ghost sm" onClick={loadQr}>Retry</button>
          </div>
        ) : (
          <img src={qrDataUrl ?? undefined} alt="Your check-in QR code" style={{ width: 186, height: 186 }} />
        )}
        <div className="divider" style={{ alignSelf: 'stretch' }} />
        <div className="col" style={{ alignItems: 'center', gap: 2 }}>
          <div className="mid" style={{ textAlign: 'center' }}>{memberName}</div>
          <div className="tiny">Member · {gymName}</div>
        </div>
      </div>

      <div className="row" style={{ justifyContent: 'center', gap: 7 }}>
        <Icon n="shield" s={14} c="var(--ink-3)" />
        <span className="tiny">Your personal code — the front desk scans it</span>
      </div>

      <button className="btn ghost block" onClick={() => setScanOpen(true)}>
        <Icon n="qr" s={17} c="var(--ink)" />
        Scan gym QR instead
      </button>

      {/* Recent check-ins */}
      <div className="card flat">
        <div className="between">
          <div className="lbl">Recent check-ins</div>
          <button className="btn ghost sm" onClick={() => navigate('/app/checkins')}>History</button>
        </div>
        {recent.length === 0 ? (
          <div className="sub wrap" style={{ marginTop: 10 }}>No check-ins yet.</div>
        ) : (
          <div className="col" style={{ marginTop: 4 }}>
            {recent.map(log => (
              <div key={log.id} className="lrow" style={{ padding: '9px 0' }}>
                <div className="li">
                  <Icon n="check" s={15} c="var(--ink-2)" />
                </div>
                <div className="col" style={{ gap: 1 }}>
                  <div className="mid">{fmtDateShort(isoToYmd(log.checkInTime))}</div>
                  <div className="tiny trunc">
                    {new Date(log.checkInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {log.branch?.displayName ? ` · ${log.branch.displayName}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {scanOpen && token && (
        <ScanSheet
          token={token}
          onClose={() => setScanOpen(false)}
          onCheckedIn={loadRecent}
        />
      )}
    </div>
  );
}

// ── Scan-the-gym-QR sheet (logic ported from the old CheckInModal) ───────────
function ScanSheet({
  token,
  onClose,
  onCheckedIn,
}: {
  token: string;
  onClose: () => void;
  onCheckedIn: () => void;
}) {
  const [isScanning, setIsScanning] = useState(false);
  const [scanState, setScanState] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [scanMessage, setScanMessage] = useState('');
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);

  const lastScannedDataRef = useRef<string | null>(null);
  const lastScanTimeRef = useRef<number>(0);

  /** True on mobile — we always use facingMode:environment and skip the device picker */
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const stopAllVideoStreams = useCallback(() => {
    try {
      const videos = Array.from(document.querySelectorAll('video'));
      videos.forEach(video => {
        const stream = video.srcObject as MediaStream | null;
        if (stream) {
          stream.getTracks().forEach(track => {
            track.stop();
            track.enabled = false;
          });
        }
        video.srcObject = null;
        video.pause();
      });
    } catch (error) {
      console.error('Error stopping video streams:', error);
    }
  }, []);

  useEffect(() => () => stopAllVideoStreams(), [stopAllVideoStreams]);

  /** Enumerate cameras only AFTER getUserMedia has been granted (desktop only). */
  const handleCameraReady = useCallback(async () => {
    if (isMobile) return;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setAvailableCameras(devices.filter(d => d.kind === 'videoinput'));
    } catch {
      // non-fatal — camera is already working
    }
  }, [isMobile]);

  const handleStartScan = async () => {
    if (isScanning) {
      setIsScanning(false);
      stopAllVideoStreams();
      setScanState('idle');
      setScanMessage('');
      return;
    }
    // Check camera permission where the Permissions API exists (iOS Safari lacks it).
    try {
      const status = await navigator.permissions.query({ name: 'camera' as PermissionName });
      if (status.state === 'denied') {
        setScanState('error');
        setScanMessage('Camera access was denied. Allow camera in your browser settings and try again.');
        return;
      }
    } catch {
      // Permissions API not supported — proceed normally
    }
    setIsScanning(true);
    setScanState('idle');
    setScanMessage('');
  };

  const handleScan = useCallback(async (qrData: string) => {
    // Prevent duplicate scans
    const now = Date.now();
    if (qrData === lastScannedDataRef.current && now - lastScanTimeRef.current < 3000) return;
    lastScannedDataRef.current = qrData;
    lastScanTimeRef.current = now;

    setIsScanning(false);
    stopAllVideoStreams();
    setScanState('processing');
    setScanMessage('Verifying…');

    try {
      const result = await mfetch(token, '/member/scan', { method: 'POST', body: { qr_code: qrData } });
      const action = result.data?.action === 'checkout' ? "You're checked out" : "You're checked in";
      setScanState('success');
      setScanMessage(action);
      onCheckedIn();
    } catch (e) {
      setScanState('error');
      setScanMessage(e instanceof MemberApiError ? e.message : 'Check-in failed');
    }
  }, [token, onCheckedIn, stopAllVideoStreams]);

  const handleCameraChange = (cameraId: string) => {
    setSelectedCameraId(cameraId);
    if (isScanning) {
      setIsScanning(false);
      stopAllVideoStreams();
      setTimeout(() => setIsScanning(true), 100);
    }
  };

  const close = () => {
    setIsScanning(false);
    stopAllVideoStreams();
    onClose();
  };

  return (
    <Sheet onClose={close}>
      <div className="between">
        <div className="col" style={{ gap: 2 }}>
          <div className="dp dp-m">Scan gym QR</div>
          <div className="sub">Point your camera at the QR at the entrance</div>
        </div>
        <button className="icon-btn" aria-label="Close" onClick={close}>
          <Icon n="x" s={16} c="var(--ink-2)" />
        </button>
      </div>

      {scanState === 'success' ? (
        <>
          <div className="card tint3" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: 26 }}>
            <div className="li gold" style={{ width: 46, height: 46, flex: '0 0 46px', borderRadius: 100 }}>
              <Icon n="check" s={24} c="var(--gold-2)" />
            </div>
            <div className="mid">{scanMessage}</div>
          </div>
          <button className="btn block" onClick={close}>Done</button>
        </>
      ) : (
        <>
          {!isMobile && availableCameras.length > 1 && (
            <select
              className="minput"
              value={selectedCameraId}
              onChange={e => handleCameraChange(e.target.value)}
              aria-label="Select camera"
            >
              {availableCameras.map((camera, i) => (
                <option key={camera.deviceId} value={camera.deviceId}>
                  {camera.label || `Camera ${i + 1}`}
                </option>
              ))}
            </select>
          )}

          <div
            style={{
              position: 'relative',
              aspectRatio: '4 / 3',
              borderRadius: 'var(--r-m)',
              overflow: 'hidden',
              background: 'var(--ink-0)',
              border: '1px solid var(--line)',
            }}
          >
            {isScanning ? (
              <>
                <QRScanner
                  isScanning={isScanning}
                  selectedCameraId={selectedCameraId}
                  onQRCodeDetected={handleScan}
                  onCameraReady={handleCameraReady}
                  onError={(err: string) => {
                    setScanState('error');
                    setScanMessage(err);
                    setIsScanning(false);
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 170,
                    height: 170,
                    border: '2px solid var(--gold-line)',
                    borderRadius: 'var(--r-l)',
                    pointerEvents: 'none',
                  }}
                />
              </>
            ) : (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 12,
                }}
              >
                {scanState === 'processing' ? (
                  <>
                    <div className="mspin" />
                    <span className="tiny" style={{ color: 'var(--on-ink)' }}>{scanMessage}</span>
                  </>
                ) : (
                  <>
                    <Icon n="qr" s={34} c="color-mix(in srgb, var(--on-ink) 45%, transparent)" />
                    <span className="tiny" style={{ color: 'color-mix(in srgb, var(--on-ink) 55%, transparent)' }}>
                      Camera is off
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {scanState === 'error' && (
            <div className="card flat" style={{ padding: '10px 12px', borderColor: 'var(--clay)', background: 'var(--clay-soft)' }}>
              <span className="sub wrap" style={{ color: 'var(--clay)' }}>{scanMessage}</span>
            </div>
          )}

          <button
            className={`btn block${isScanning ? ' ghost' : ' gold'}`}
            disabled={scanState === 'processing'}
            onClick={handleStartScan}
          >
            {isScanning ? 'Stop scanning' : scanState === 'error' ? 'Try again' : 'Start scanning'}
          </button>
        </>
      )}
    </Sheet>
  );
}
